import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, Outlet, createFileRoute, useNavigate, useParams, useRouter } from '@tanstack/react-router'
import { AlertTriangleIcon, PaperclipIcon } from 'lucide-react'
import type { SupportStatus, SupportThread } from '@hedgehog/shared'
import { opsApi } from '@/components/ops/api'
import { ErrorState, LoadingState } from '@/components/app/states'
import { formatAge } from '@/components/app/format'
import { Button } from '@/components/ui/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty'
import { useInterval } from '@/hooks/use-interval'
import { cn } from '@/lib/utils'
import { typing, UndoContext, type Undo } from '@/components/ops/support'

// HO-4 support mailbox: conversation list (this layout) + the open conversation (child route).
// Keyboard first: j/k move, o/Enter open, 1-4 switch tabs, ? lists every shortcut.

const TABS: { status: SupportStatus; label: string; counted: boolean }[] = [
  { status: 'open', label: 'Open', counted: true },
  { status: 'waiting', label: 'Waiting', counted: true },
  { status: 'closed', label: 'Closed', counted: false },
  { status: 'spam', label: 'Spam', counted: false },
]

export const Route = createFileRoute('/ops/support')({
  validateSearch: (s: Record<string, unknown>): { status: SupportStatus } => ({
    status: TABS.some((t) => t.status === s.status) ? (s.status as SupportStatus) : 'open',
  }),
  loaderDeps: ({ search }) => ({ status: search.status }),
  loader: ({ deps }) => opsApi<{ threads: SupportThread[]; counts: Record<SupportStatus, number> }>(`/ops/support?status=${deps.status}`),
  head: () => ({ meta: [{ title: 'Support · Ops' }] }),
  component: SupportLayout,
  pendingComponent: () => <LoadingState label="Loading support" />,
  errorComponent: ({ error }) => <ErrorState error={error} title="Could not load support" />,
})

function SupportLayout() {
  const { threads, counts } = Route.useLoaderData()
  const { status } = Route.useSearch()
  const { threadId } = useParams({ strict: false }) as { threadId?: string }
  const navigate = useNavigate()
  const router = useRouter()
  const [undo, setUndo] = useState<Undo | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)

  useInterval(() => router.invalidate({ filter: (m) => m.routeId === Route.id }), 60_000)

  const showUndo = useCallback((u: Undo) => {
    clearTimeout(timer.current)
    setUndo(u)
    timer.current = setTimeout(() => setUndo(null), 6000)
  }, [])
  const runUndo = useCallback(async () => {
    if (!undo) return
    setUndo(null)
    await undo.run()
    await router.invalidate()
  }, [undo, router])

  const open = useCallback((id: string) => navigate({ to: '/ops/support/$threadId', params: { threadId: id }, search: { status } }), [navigate, status])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && undo && !(e.target as HTMLElement).closest('input, textarea')) {
        e.preventDefault()
        void runUndo()
        return
      }
      if (typing(e)) return
      const i = threads.findIndex((t) => t.id === threadId)
      if (e.key === 'j' || e.key === 'k') {
        const next = threads[i < 0 ? 0 : Math.min(threads.length - 1, Math.max(0, i + (e.key === 'j' ? 1 : -1)))]
        if (next) open(next.id)
      } else if ((e.key === 'o' || e.key === 'Enter') && !threadId && threads[0]) open(threads[0].id)
      else if (['1', '2', '3', '4'].includes(e.key)) navigate({ to: '/ops/support', search: { status: TABS[Number(e.key) - 1].status } })
      else return
      e.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [threads, threadId, open, navigate, undo, runUndo])

  return (
    <UndoContext.Provider value={showUndo}>
      <div className="grid gap-4 xl:h-[calc(100dvh-2.75rem-2rem)] xl:grid-cols-[20rem_minmax(0,1fr)]">
        <div className={cn('flex min-h-0 flex-col gap-3', threadId && 'hidden xl:flex')}>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <h1 className="text-base font-semibold">Support</h1>
            <nav aria-label="Conversation status" className="flex gap-1">
              {TABS.map((t) => (
                <Link
                  key={t.status}
                  to="/ops/support"
                  search={{ status: t.status }}
                  aria-current={t.status === status ? 'page' : undefined}
                  className={cn(
                    'rounded-md px-2 py-1 whitespace-nowrap text-muted-foreground outline-none hover:bg-muted hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50',
                    t.status === status && 'bg-muted font-medium text-foreground',
                  )}
                >
                  {t.label}
                  {t.counted && counts[t.status] > 0 && <span className="ml-1 tabular-nums text-muted-foreground">{counts[t.status]}</span>}
                </Link>
              ))}
            </nav>
          </div>
          {threads.length === 0 ? (
            <Empty className="border border-border">
              <EmptyHeader>
                <EmptyTitle>{status === 'open' ? 'No open emails' : `Nothing in ${status}`}</EmptyTitle>
                <EmptyDescription>Emails to the support address land here with a drafted reply.</EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <ul className="min-h-0 flex-1 divide-y divide-border overflow-y-auto rounded-lg border border-border">
              {threads.map((t) => (
                <ThreadRowItem key={t.id} t={t} status={status} active={t.id === threadId} />
              ))}
            </ul>
          )}
        </div>
        <div className={cn('min-h-0', !threadId && 'hidden xl:block')}>
          <Outlet />
        </div>
      </div>
      {undo && (
        <div role="status" className="fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-lg bg-foreground px-4 py-2 text-background shadow-lg">
          <span>{undo.label}</span>
          <Button size="sm" variant="secondary" onClick={() => void runUndo()}>
            Undo
          </Button>
        </div>
      )}
    </UndoContext.Provider>
  )
}

function ThreadRowItem({ t, status, active }: { t: SupportThread; status: SupportStatus; active: boolean }) {
  return (
    <li className={cn(t.escalate && 'border-l-2 border-l-destructive')}>
      <Link
        to="/ops/support/$threadId"
        params={{ threadId: t.id }}
        search={{ status }}
        preload="intent"
        title={t.subject}
        aria-current={active ? 'page' : undefined}
        className={cn('flex flex-col gap-0.5 px-3 py-2 outline-none hover:bg-muted/60 focus-visible:bg-muted', active && 'bg-muted')}
      >
        <span className="flex items-center gap-1.5">
          {t.lastDirection === 'in' && status === 'open' && <span aria-label="Customer wrote last" className="size-1.5 shrink-0 rounded-full bg-primary" />}
          {t.escalate && <AlertTriangleIcon aria-label="Needs a person" className="size-3.5 shrink-0 text-destructive" />}
          <span className="min-w-0 flex-1 truncate font-medium">{t.customer}</span>
          {t.hasAttachments && <PaperclipIcon aria-label="Has attachments" className="size-3.5 shrink-0 text-muted-foreground" />}
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{formatAge(t.updatedAt)}</span>
        </span>
        <span className="truncate text-muted-foreground">{t.summary ?? t.subject}</span>
      </Link>
    </li>
  )
}
